import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SelfPromoPanel from "@/components/SelfPromoPanel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Shield, Clock, CheckCircle2, XCircle, Users, Gift, Crown, DollarSign, Trophy, BarChart3, Mail, Download, FlaskConical, PlayCircle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { VideoUploader } from "@/components/VideoUploader";

const ANGLE_COLORS = { "Problem/Solution": "#6366F1", "Behind The Scenes": "#38BDF8", "Value Breakdown": "#F59E0B", "Social Proof": "#10B981" };
const PLATFORM_COLORS = { twitter: "#38BDF8", facebook: "#6366F1", linkedin: "#0EA5E9", instagram: "#EC4899", youtube: "#EF4444" };
const STATUS_STYLE = { approved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40", pending: "bg-amber-500/15 text-amber-400 border-amber-500/40", rejected: "bg-rose-500/15 text-rose-400 border-rose-500/40" };
const money = (n) => `$${(n ?? 0).toFixed(2)}`;
const fmt = (iso) => iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

function PlanBadge({ u }) {
  const TIER_LABEL = { creator: "Creator", growth: "E-comm Growth", agency: "Agency", pro: "Creator (Legacy)", starter: "Creator" };
  const label = u.subscription_status === "active"
    ? `${TIER_LABEL[u.plan_tier] || u.plan_tier || "Creator"}${u.in_paid_trial ? " · trial" : ""}`
    : (u.subscription_status || "inactive");
  const cls = u.subscription_status === "active" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" : "bg-muted text-muted-foreground border-border";
  return <Badge className={`border text-[10px] ${cls}`}>{label}</Badge>;
}

export default function AdminPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [funnelBy, setFunnelBy] = useState("angle");
  const [forbidden, setForbidden] = useState(false);
  const [signups, setSignups] = useState(null);
  const [affiliates, setAffiliates] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [rateEdits, setRateEdits] = useState({});
  const [txnEdits, setTxnEdits] = useState({});
  const [funnelStats, setFunnelStats] = useState(null);
  const [leads, setLeads] = useState(null);
  const [emailDomain, setEmailDomain] = useState(null);
  const [domainBusy, setDomainBusy] = useState("");
  const [demoVideo, setDemoVideo] = useState("");
  const [demoBusy, setDemoBusy] = useState(false);
  const saveDemoVideo = async () => {
    setDemoBusy(true);
    try {
      const r = await api.put("/site/settings", { demo_video_url: demoVideo });
      setDemoVideo(r.data.demo_video_url);
      toast.success(r.data.demo_video_url ? "Saved — a Watch demo button now shows on your homepage." : "Saved — demo video hidden.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
    setDemoBusy(false);
  };

  const loadEmailDomain = () => api.get("/admin/email-domain").then((r) => setEmailDomain(r.data)).catch(() => {});

  const emailDomainAction = async (action) => {
    setDomainBusy(action);
    try {
      const r = await api.post(`/admin/email-domain/${action}`);
      setEmailDomain((d) => ({ ...(d || {}), domain: r.data.domain, records: r.data.records, full_access: true, configured: true }));
      toast.success(action === "add" ? "Domain added — add the DNS records below, then Verify." : "Verification requested. Re-check after DNS propagates.");
      loadEmailDomain();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed.");
    } finally { setDomainBusy(""); }
  };

  const loadAff = () => {
    api.get("/admin/affiliates").then((r) => setAffiliates(r.data)).catch(() => {});
    api.get("/admin/payouts").then((r) => setPayouts(r.data)).catch(() => {});
  };
  useEffect(() => {
    api.get("/promo/posts?limit=40").then((r) => setPosts(r.data)).catch((e) => { if (e?.response?.status === 403) setForbidden(true); });
    api.get("/promo/analytics/timeseries?weeks=6").then((r) => setFunnel(r.data)).catch(() => {});
    api.get("/admin/signups").then((r) => setSignups(r.data)).catch(() => {});
    api.get("/admin/funnel-analytics").then((r) => setFunnelStats(r.data)).catch(() => {});
    api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/site/settings").then((r) => setDemoVideo(r.data?.demo_video_url || "")).catch(() => {});
    loadEmailDomain();
    loadAff();
  }, []);

  const downloadLeads = async () => {
    try {
      const r = await api.get("/admin/leads.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "autogridsocial-leads.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Leads exported");
    } catch (e) { toast.error("Could not export leads"); }
  };

  if (forbidden || (user && user.is_admin === false)) {
    return (
      <div className="py-24 text-center" data-testid="admin-forbidden">
        <Shield className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
        <h2 className="text-xl font-display font-bold">Admin only</h2>
        <p className="text-muted-foreground mt-1">This area is restricted to AutoGridSocial administrators.</p>
      </div>
    );
  }

  const saveRate = async (uid) => {
    const v = rateEdits[uid];
    if (v === undefined || v === "") return;
    try { await api.post(`/admin/affiliates/${uid}/rate`, { rate: parseFloat(v) }); toast.success("Rate updated"); loadAff(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggleBan = async (uid, banned) => {
    try { await api.post(`/admin/affiliates/${uid}/ban`, { banned }); toast.success(banned ? "Affiliate flagged/banned" : "Affiliate re-enabled"); loadAff(); }
    catch (e) { toast.error("Failed"); }
  };
  const approvePayout = async (pid) => {
    try { await api.post(`/admin/payouts/${pid}/approve`, { txn_id: txnEdits[pid] || "" }); toast.success("Payout approved & paid"); loadAff(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const denyPayout = async (pid) => {
    try { await api.post(`/admin/payouts/${pid}/deny`, { reason: "denied by admin" }); toast.message("Payout denied"); loadAff(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-8" data-testid="admin-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-accent/20 flex items-center justify-center"><Shield className="h-5 w-5 text-accent" /></div>
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Owner Console</h1>
          <p className="text-muted-foreground mt-1">Signups, affiliate network & self-promotion. Only you can see this.</p>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-5" data-testid="admin-demo-video">
        <h3 className="font-semibold flex items-center gap-2"><PlayCircle className="h-4 w-4 text-accent" /> Landing demo video</h3>
        <p className="text-xs text-muted-foreground mt-1">Paste a YouTube, Vimeo or direct .mp4 link. A "Watch demo" button appears on your homepage hero. Leave blank to hide it.</p>
        <div className="flex flex-col sm:flex-row gap-2 mt-3">
          <Input data-testid="admin-demo-video-input" value={demoVideo} onChange={(e) => setDemoVideo(e.target.value)}
            placeholder="https://youtu.be/... or https://.../demo.mp4" className="flex-1" />
          <Button data-testid="admin-demo-video-save" onClick={saveDemoVideo} disabled={demoBusy} className="bg-primary hover:bg-primary/90 text-white shrink-0">
            {demoBusy ? "Saving…" : "Save"}
          </Button>
        </div>
        <div className="mt-3 pt-3 border-t border-border">
          <p className="text-xs text-muted-foreground mb-2">…or upload a video file directly (stored for you, no YouTube needed):</p>
          <VideoUploader onDone={(url) => setDemoVideo(url)} />
        </div>
      </div>

      <Tabs defaultValue="signups">
        <TabsList data-testid="admin-tabs" className="bg-secondary/50 flex w-full justify-start overflow-x-auto max-w-full">
          <TabsTrigger value="signups" data-testid="admin-tab-signups" className="shrink-0"><Users className="h-4 w-4 mr-1.5" /> Signups{signups ? ` (${signups.total})` : ""}</TabsTrigger>
          <TabsTrigger value="affiliates" data-testid="admin-tab-affiliates" className="shrink-0"><Gift className="h-4 w-4 mr-1.5" /> Affiliates{affiliates ? ` (${affiliates.total})` : ""}</TabsTrigger>
          <TabsTrigger value="promo" data-testid="admin-tab-promo" className="shrink-0"><Clock className="h-4 w-4 mr-1.5" /> Self-Promo</TabsTrigger>
          <TabsTrigger value="funnel" data-testid="admin-tab-funnel" className="shrink-0"><BarChart3 className="h-4 w-4 mr-1.5" /> Funnel</TabsTrigger>
          <TabsTrigger value="leads" data-testid="admin-tab-leads" className="shrink-0"><Mail className="h-4 w-4 mr-1.5" /> Leads{leads ? ` (${leads.total})` : ""}</TabsTrigger>
        </TabsList>

        {/* SIGNUPS */}
        <TabsContent value="signups" className="mt-6">
          <div className="rounded-xl border border-border bg-card p-6" data-testid="admin-signups-panel">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> All signups {signups ? `· ${signups.total}` : ""}</h3>
            {!signups ? <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p> : signups.signups.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No signups yet.</p> : (
              <div className="rounded-lg border border-border overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-secondary/40 text-muted-foreground">
                    <th className="text-left font-medium px-3 py-2">Joined</th>
                    <th className="text-left font-medium px-3 py-2">Name</th>
                    <th className="text-left font-medium px-3 py-2">Email</th>
                    <th className="text-center font-medium px-3 py-2">Status</th>
                    <th className="text-left font-medium px-3 py-2">Referred by</th>
                  </tr></thead>
                  <tbody>
                    {signups.signups.map((u) => (
                      <tr key={u.user_id} className="border-t border-border" data-testid={`admin-signup-row-${u.user_id}`}>
                        <td className="px-3 py-2 font-mono whitespace-nowrap text-muted-foreground">{fmt(u.created_at)}</td>
                        <td className="px-3 py-2">{u.name || "—"}</td>
                        <td className="px-3 py-2 font-mono">{u.email}</td>
                        <td className="px-3 py-2 text-center"><PlanBadge u={u} /></td>
                        <td className="px-3 py-2 text-muted-foreground">{u.referred_by_name || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* AFFILIATES */}
        <TabsContent value="affiliates" className="mt-6 space-y-6">
          {/* Leaderboard */}
          {affiliates && affiliates.affiliates.length > 0 && (() => {
            const ranked = [...affiliates.affiliates]
              .map((a) => ({ ...a, total: (a.owed || 0) + (a.paid || 0) }))
              .sort((x, y) => (y.total - x.total) || (y.active_paid_subs - x.active_paid_subs))
              .slice(0, 5);
            const medal = ["text-amber-400", "text-slate-300", "text-orange-400"];
            return (
              <div className="rounded-xl border border-border bg-card p-6" data-testid="admin-leaderboard">
                <h3 className="font-semibold mb-4 flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-400" /> Top affiliates</h3>
                <div className="space-y-2">
                  {ranked.map((a, i) => (
                    <div key={a.user_id} className="flex items-center gap-3 rounded-lg border border-border bg-secondary/20 px-4 py-3" data-testid={`admin-leaderboard-row-${i}`}>
                      <span className={`font-display font-bold text-lg w-6 text-center ${medal[i] || "text-muted-foreground"}`}>{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{a.name || a.email}</div>
                        <div className="text-xs text-muted-foreground font-mono truncate">/ref/{a.slug || a.code}</div>
                      </div>
                      <div className="text-center hidden sm:block">
                        <div className="text-sm font-semibold">{a.active_paid_subs}</div>
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wide">subs</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm font-semibold">{a.trial_signups}</div>
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wide">trials</div>
                      </div>
                      <div className="text-right w-24">
                        <div className="text-sm font-semibold text-emerald-400 font-mono">{money(a.total)}</div>
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wide">lifetime</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          <div className="rounded-xl border border-border bg-card p-6" data-testid="admin-affiliates-panel">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><Gift className="h-4 w-4 text-primary" /> Affiliate network</h3>
            {!affiliates ? <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p> : affiliates.affiliates.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No affiliates with activity yet.</p> : (
              <div className="rounded-lg border border-border overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-secondary/40 text-muted-foreground">
                    <th className="text-left font-medium px-3 py-2">Affiliate</th>
                    <th className="text-center font-medium px-3 py-2">Clicks</th>
                    <th className="text-center font-medium px-3 py-2">Trials</th>
                    <th className="text-center font-medium px-3 py-2">Paid subs</th>
                    <th className="text-right font-medium px-3 py-2">Owed</th>
                    <th className="text-right font-medium px-3 py-2">Paid</th>
                    <th className="text-center font-medium px-3 py-2">Rate</th>
                    <th className="text-center font-medium px-3 py-2">Banned</th>
                  </tr></thead>
                  <tbody>
                    {affiliates.affiliates.map((a) => (
                      <tr key={a.user_id} className="border-t border-border" data-testid={`admin-affiliate-row-${a.user_id}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium">{a.name || a.email}</div>
                          <div className="text-muted-foreground font-mono">/ref/{a.slug || a.code}</div>
                        </td>
                        <td className="px-3 py-2 text-center">{a.clicks}</td>
                        <td className="px-3 py-2 text-center">{a.trial_signups}</td>
                        <td className="px-3 py-2 text-center">{a.active_paid_subs >= 10 ? <span className="text-amber-400 inline-flex items-center gap-1"><Crown className="h-3 w-3" />{a.active_paid_subs}</span> : a.active_paid_subs}</td>
                        <td className="px-3 py-2 text-right font-mono">{money(a.owed)}</td>
                        <td className="px-3 py-2 text-right font-mono text-muted-foreground">{money(a.paid)}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1 justify-center">
                            <Input defaultValue={(a.commission_rate * 100).toFixed(0)} onChange={(e) => setRateEdits({ ...rateEdits, [a.user_id]: e.target.value })}
                              className="h-7 w-14 text-center text-xs" data-testid={`admin-rate-input-${a.user_id}`} />
                            <span className="text-muted-foreground">%</span>
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-primary" onClick={() => saveRate(a.user_id)} data-testid={`admin-rate-save-${a.user_id}`}>Set</Button>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Switch checked={!!a.banned} onCheckedChange={(v) => toggleBan(a.user_id, v)} data-testid={`admin-ban-${a.user_id}`} className="scale-75" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Payout requests */}
          <div className="rounded-xl border border-border bg-card p-6" data-testid="admin-payouts-panel">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><DollarSign className="h-4 w-4 text-primary" /> Payout requests</h3>
            {payouts.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No payout requests.</p> : (
              <div className="space-y-2">
                {payouts.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 flex-wrap rounded-lg border border-border bg-secondary/20 px-4 py-3 text-sm" data-testid={`admin-payout-${p.id}`}>
                    <span className="font-mono text-xs text-muted-foreground">{fmt(p.created_at)}</span>
                    <span className="font-medium">{p.affiliate_email}</span>
                    <span className="font-mono">{money(p.amount)}</span>
                    <Badge variant="outline" className="text-[10px] border-border capitalize">{p.method === "stripe" ? "Stripe (auto)" : `${p.method}: ${p.email}`}</Badge>
                    <Badge className={p.status === "paid" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" : p.status === "denied" ? "bg-rose-500/15 text-rose-400 border-rose-500/40" : "bg-amber-500/15 text-amber-400 border-amber-500/40"}>{p.status}</Badge>
                    {p.txn_id && <span className="font-mono text-[10px] text-muted-foreground">{p.txn_id}</span>}
                    {p.status === "requested" && (
                      <div className="flex items-center gap-2 ml-auto">
                        {p.method !== "stripe" && (
                          <Input placeholder="txn id" onChange={(e) => setTxnEdits({ ...txnEdits, [p.id]: e.target.value })} className="h-7 w-28 text-xs" data-testid={`admin-payout-txn-${p.id}`} />
                        )}
                        <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-600/90 text-white" onClick={() => approvePayout(p.id)} data-testid={`admin-payout-approve-${p.id}`}>{p.method === "stripe" ? "Approve & pay via Stripe" : "Mark paid"}</Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-400" onClick={() => denyPayout(p.id)} data-testid={`admin-payout-deny-${p.id}`}>Deny</Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* SELF-PROMO */}
        <TabsContent value="promo" className="mt-6 space-y-8">
          {funnel && (
            <div className="rounded-xl border border-border bg-card p-6" data-testid="admin-funnel-chart">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold">Trial signups from self-promo</h3>
                  <p className="text-xs text-muted-foreground mt-1">Which {funnelBy === "angle" ? "angle" : "platform"} converts — last 6 weeks</p>
                </div>
                <div className="flex items-center gap-1 rounded-lg border border-border bg-secondary/40 p-0.5">
                  <button data-testid="admin-funnel-angle" onClick={() => setFunnelBy("angle")} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${funnelBy === "angle" ? "bg-primary text-white" : "text-muted-foreground"}`}>Angle</button>
                  <button data-testid="admin-funnel-platform" onClick={() => setFunnelBy("platform")} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${funnelBy === "platform" ? "bg-primary text-white" : "text-muted-foreground"}`}>Platform</button>
                </div>
              </div>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                  <BarChart data={funnelBy === "angle" ? funnel.series : funnel.series_platform} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                    <XAxis dataKey="week" stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1F2937", borderRadius: 10, color: "#F9FAFB" }} cursor={{ fill: "rgba(99,102,241,0.08)" }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {(funnelBy === "angle" ? funnel.angles : funnel.platforms).map((k) => (
                      <Bar key={k} dataKey={k} stackId="s" fill={(funnelBy === "angle" ? ANGLE_COLORS[k] : PLATFORM_COLORS[k]) || "#6366F1"} radius={[2, 2, 0, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="rounded-xl border border-border bg-card p-6" data-testid="admin-promo-posts">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> Generated posts &amp; publish times</h3>
            {posts.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No promo posts generated yet.</p> : (
              <div className="rounded-lg border border-border overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-secondary/40 text-muted-foreground">
                    <th className="text-left font-medium px-3 py-2">Generated</th>
                    <th className="text-left font-medium px-3 py-2">Platform</th>
                    <th className="text-left font-medium px-3 py-2">Angle</th>
                    <th className="text-left font-medium px-3 py-2">Post</th>
                    <th className="text-center font-medium px-3 py-2">Hook</th>
                    <th className="text-center font-medium px-3 py-2">Status</th>
                    <th className="text-center font-medium px-3 py-2">Published</th>
                  </tr></thead>
                  <tbody>
                    {posts.map((p) => (
                      <tr key={p.id} className="border-t border-border" data-testid={`admin-promo-row-${p.id}`}>
                        <td className="px-3 py-2 font-mono whitespace-nowrap text-muted-foreground">{fmt(p.created_at)}</td>
                        <td className="px-3 py-2 capitalize">{p.platform}</td>
                        <td className="px-3 py-2 font-mono text-muted-foreground">{p.angle_used}</td>
                        <td className="px-3 py-2 max-w-[280px] truncate">{p.post_text}</td>
                        <td className="px-3 py-2 text-center font-mono">{p.hook_score ?? "—"}</td>
                        <td className="px-3 py-2 text-center"><Badge className={`border text-[10px] ${STATUS_STYLE[p.status] || "border-border text-muted-foreground"}`}>{p.status}</Badge></td>
                        <td className="px-3 py-2 text-center">{p.dispatched ? <CheckCircle2 className="h-4 w-4 text-emerald-400 inline" /> : <XCircle className="h-4 w-4 text-muted-foreground/50 inline" />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <SelfPromoPanel />
        </TabsContent>

        {/* FUNNEL ANALYTICS */}
        <TabsContent value="funnel" className="mt-6 space-y-6">
          {/* A/B hero-hook test */}
          {funnelStats && funnelStats.by_variant && funnelStats.by_variant.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-6" data-testid="admin-ab-panel">
              <h3 className="font-semibold mb-1 flex items-center gap-2"><FlaskConical className="h-4 w-4 text-primary" /> Hero headline A/B test</h3>
              <p className="text-xs text-muted-foreground mb-5">Two hero hooks on <span className="font-mono">/start</span>. Winner = highest lead conversion.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {funnelStats.by_variant.map((v) => {
                  const isWinner = funnelStats.winner === v.variant;
                  const HOOKS = {
                    A: "Turn Text & Graphic Assets Into a 30-Day Automated Social Queue",
                    B: "Stop Manually Posting — Build a Full Month of Social in 5 Minutes",
                  };
                  return (
                    <div key={v.variant} className={`rounded-xl border p-5 relative ${isWinner ? "border-emerald-500/50 bg-emerald-500/5" : "border-border bg-secondary/20"}`} data-testid={`admin-ab-variant-${v.variant}`}>
                      {isWinner && <Badge className="absolute -top-3 right-4 bg-emerald-600 text-white border-0 text-[10px]"><Trophy className="h-3 w-3 mr-1" /> Winner</Badge>}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-display font-bold text-lg">Variant {v.variant}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mb-4 leading-relaxed">“{HOOKS[v.variant] || "—"}”</p>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div><div className="font-display text-xl font-bold">{v.visit}</div><div className="text-[10px] text-muted-foreground uppercase tracking-wide">Visits</div></div>
                        <div><div className="font-display text-xl font-bold">{v.lead}</div><div className="text-[10px] text-muted-foreground uppercase tracking-wide">Leads</div></div>
                        <div><div className="font-display text-xl font-bold text-primary">{v.lead_conversion}%</div><div className="text-[10px] text-muted-foreground uppercase tracking-wide">Lead conv.</div></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-border bg-card p-6" data-testid="admin-funnel-analytics">
            <h3 className="font-semibold mb-1 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /> Acquisition funnel by traffic source</h3>
            <p className="text-xs text-muted-foreground mb-5">Scan → Signup → First Campaign per <span className="font-mono">?src=</span> — see which ads/reels actually activate.</p>

            {funnelStats?.funnel_stages && (
              <div className="mb-6 rounded-lg border border-border bg-secondary/10 p-4" data-testid="admin-funnel-stages">
                <div className="flex items-center justify-between gap-2">
                  {funnelStats.funnel_stages.map((st, i) => {
                    const top = funnelStats.funnel_stages[0].count || 1;
                    const pct = Math.round((st.count / top) * 100);
                    return (
                      <React.Fragment key={st.stage}>
                        <div className="flex-1 text-center">
                          <div className="font-display text-2xl font-bold">{st.count}</div>
                          <div className="text-[11px] text-muted-foreground uppercase tracking-wide">{st.stage}</div>
                          <div className="mt-1.5 h-1 rounded-full bg-secondary/60 overflow-hidden"><div className="h-full bg-primary" style={{ width: `${pct}%` }} /></div>
                        </div>
                        {i < funnelStats.funnel_stages.length - 1 && <span className="text-muted-foreground/50 text-lg">→</span>}
                      </React.Fragment>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground mt-3 text-center">
                  Scan→Signup <span className="text-foreground font-medium">{funnelStats.totals.signup_conversion}%</span> · Signup→First Campaign (activation) <span className="text-foreground font-medium">{funnelStats.totals.activation}%</span>
                </p>
              </div>
            )}

            {(!funnelStats || funnelStats.by_src.length === 0) ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No funnel traffic yet. Share <span className="font-mono">/start?src=reel</span> from your bio links to start tracking.</p>
            ) : (
              <div className="rounded-lg border border-border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="bg-secondary/40 text-left text-muted-foreground">
                    <th className="px-4 py-2.5 font-medium">Source</th>
                    <th className="px-4 py-2.5 font-medium text-center">Visits</th>
                    <th className="px-4 py-2.5 font-medium text-center">Scans</th>
                    <th className="px-4 py-2.5 font-medium text-center">Signups</th>
                    <th className="px-4 py-2.5 font-medium text-center">1st Campaign</th>
                    <th className="px-4 py-2.5 font-medium text-center">Activation</th>
                  </tr></thead>
                  <tbody>
                    {funnelStats.by_src.map((r) => (
                      <tr key={r.src} className="border-t border-border" data-testid={`admin-funnel-row-${r.src}`}>
                        <td className="px-4 py-2.5 font-mono">{r.src}</td>
                        <td className="px-4 py-2.5 text-center">{r.visit}</td>
                        <td className="px-4 py-2.5 text-center">{r.url_scan}</td>
                        <td className="px-4 py-2.5 text-center">{r.signup}</td>
                        <td className="px-4 py-2.5 text-center">{r.first_campaign}</td>
                        <td className="px-4 py-2.5 text-center"><Badge className="border-primary/40 text-primary text-[11px] bg-primary/10">{r.activation}%</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* LEADS */}
        <TabsContent value="leads" className="mt-6">
          <div className="rounded-xl border border-border bg-card p-6" data-testid="admin-leads-panel">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <div>
                <h3 className="font-semibold flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /> Captured leads {leads ? `· ${leads.total}` : ""}</h3>
                <p className="text-xs text-muted-foreground mt-1">Emails captured from the <span className="font-mono">/start</span> funnel. Export to import into your email tool.</p>
              </div>
              <Button size="sm" onClick={downloadLeads} disabled={!leads || leads.total === 0} className="bg-primary hover:bg-primary/90 text-white h-9 text-xs" data-testid="admin-leads-export">
                <Download className="h-4 w-4 mr-1.5" /> Download Leads CSV
              </Button>
            </div>
            {!leads ? <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p> : leads.leads.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No leads captured yet. Share <span className="font-mono">/start</span> to start collecting emails.</p> : (
              <div className="rounded-lg border border-border overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-secondary/40 text-muted-foreground">
                    <th className="text-left font-medium px-3 py-2">Captured</th>
                    <th className="text-left font-medium px-3 py-2">Name</th>
                    <th className="text-left font-medium px-3 py-2">Email</th>
                    <th className="text-left font-medium px-3 py-2">Source</th>
                  </tr></thead>
                  <tbody>
                    {leads.leads.map((l, i) => (
                      <tr key={l.email || i} className="border-t border-border" data-testid={`admin-lead-row-${i}`}>
                        <td className="px-3 py-2 font-mono whitespace-nowrap text-muted-foreground">{fmt(l.created_at)}</td>
                        <td className="px-3 py-2">{l.name || "—"}</td>
                        <td className="px-3 py-2 font-mono">{l.email}</td>
                        <td className="px-3 py-2 text-muted-foreground">{l.source || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Email sending domain (Resend) */}
          <div className="rounded-xl border border-border bg-card p-6 mt-6" data-testid="admin-email-domain-panel">
            <h3 className="font-semibold flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /> Email sending domain</h3>
            <p className="text-xs text-muted-foreground mt-1 mb-4">Verify your domain so lead & affiliate emails deliver to everyone (not just verified test addresses).</p>
            {!emailDomain ? (
              <p className="text-sm text-muted-foreground py-4">Loading…</p>
            ) : !emailDomain.configured ? (
              <p className="text-sm text-amber-400" data-testid="admin-email-domain-message">{emailDomain.message}</p>
            ) : !emailDomain.full_access ? (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4" data-testid="admin-email-domain-restricted">
                <p className="text-sm text-amber-300">{emailDomain.message}</p>
                <p className="text-xs text-muted-foreground mt-2">Create a full-access key at resend.com/api-keys, then send it over and I'll wire it in.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm font-mono">{emailDomain.target}</span>
                  {emailDomain.domain ? (
                    <Badge className={`border text-[10px] ${emailDomain.domain.status === "verified" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-sky-500/15 text-sky-400 border-sky-500/30"}`}
                      data-testid="admin-email-domain-status">{emailDomain.domain.status || "pending"}</Badge>
                  ) : (
                    <Badge className="border border-border bg-secondary text-muted-foreground text-[10px]">not added</Badge>
                  )}
                  {!emailDomain.domain ? (
                    <Button size="sm" onClick={() => emailDomainAction("add")} disabled={domainBusy === "add"} className="bg-primary hover:bg-primary/90 text-white h-8 text-xs" data-testid="admin-email-domain-add">
                      {domainBusy === "add" ? <CheckCircle2 className="h-3.5 w-3.5 mr-1.5 animate-pulse" /> : null} Add domain
                    </Button>
                  ) : (
                    <Button size="sm" onClick={() => emailDomainAction("verify")} disabled={domainBusy === "verify"} className="bg-emerald-600 hover:bg-emerald-500 text-white h-8 text-xs" data-testid="admin-email-domain-verify">
                      {domainBusy === "verify" ? <CheckCircle2 className="h-3.5 w-3.5 mr-1.5 animate-pulse" /> : null} Verify
                    </Button>
                  )}
                </div>
                {emailDomain.records && emailDomain.records.length > 0 && (
                  <div className="rounded-lg border border-border overflow-x-auto" data-testid="admin-email-domain-records">
                    <p className="text-xs text-muted-foreground px-3 pt-3">Add these records at your DNS host, then click Verify:</p>
                    <table className="w-full text-xs mt-2">
                      <thead><tr className="bg-secondary/40 text-muted-foreground">
                        <th className="text-left font-medium px-3 py-2">Type</th>
                        <th className="text-left font-medium px-3 py-2">Name</th>
                        <th className="text-left font-medium px-3 py-2">Value</th>
                      </tr></thead>
                      <tbody>
                        {emailDomain.records.map((rec, i) => (
                          <tr key={i} className="border-t border-border align-top">
                            <td className="px-3 py-2 font-mono">{rec.type || rec.record}</td>
                            <td className="px-3 py-2 font-mono break-all">{rec.name}</td>
                            <td className="px-3 py-2 font-mono break-all">{rec.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
